// frontend/src/pages/UserManagementPage.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-06-16
// Updated:      2026-09-25 10:45 BST
//
// Desktop resize regression fix (2026-09-25):
//   - Removed the viewport-derived ReactGridLayout `maxRows` ceiling.
//   - Desktop widgets may now grow vertically beyond the initial viewport and the page scrolls naturally.
//   - Layout persistence preserves x/y/w/h while preventing negative y positions.
//   - Page-owned widget positioning is no longer forced back inside viewport rows.
//   - Existing mobile single-column presentation and mobile sizing behaviour are unchanged.
//
// Description:  User Management page with draggable widgets, persistent layout, Add Widget button, and widget close buttons.
//               Final layout: full-width content with consistent horizontal padding and aligned grid.
//               RBAC Console enhanced with editable policy drafts, role comparison, directory views, and effective-access inspection.
//
// Mobile Responsiveness (2026-09-04):
//               • Overview widgets render as a natural single-column stream at <=768px.
//               • Mobile viewing never writes mobile widget positions/sizes back to desktop storage.
//               • RBAC navigation, role/area selectors, policy editor, directory, effective-access
//                 and audit views reflow for touch/mobile use without changing RBAC state or APIs.
//               • Desktop react-grid-layout and desktop RBAC presentation remain unchanged.

import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import GridLayout from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

import UserFormWidget from "../widgets/UserFormWidget";
import UserListWidget from "../widgets/UserListWidget";
import RoleStatsWidget from "../widgets/RoleStatsWidget.jsx";
import AdvancedUserListWidget from "../widgets/advanced_user_list.jsx";
import LoggedInUsersWidget from "../widgets/LoggedInUsersWidget.jsx";

import AddWidgetModal from "../components/AddWidgetModal";
import WidgetWrapper from "../components/WidgetWrapper";
import ManageUserModal from "../components/ManageUserModal";

import { markWidgetUsedIn } from "../widgets/WidgetRegistry";
import axios from "axios";
import { useTheme } from "../context/ThemeContext.jsx";
import { useAuth } from "../context/AuthContext";

const STORAGE_KEY_LAYOUT = "userManagementLayout";
const STORAGE_KEY_WIDGETS = "userManagementWidgets";
const STORAGE_KEY_ACTIVE_TAB = "userManagementActiveTab";
const STORAGE_KEY_RBAC_ROLE = "userManagementRbacRole";
const STORAGE_KEY_RBAC_AREA = "userManagementRbacArea";
const STORAGE_KEY_RBAC_VIEW = "userManagementRbacView";
const STORAGE_KEY_RBAC_MATRIX = "userManagementRbacDraftMatrixV2";
const STORAGE_KEY_RBAC_COMPARE_ROLE = "userManagementRbacCompareRole";
const MOBILE_BREAKPOINT = 768;

function useIsMobile(breakpoint = MOBILE_BREAKPOINT) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(`(max-width: ${breakpoint}px)`).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;

    const mediaQuery = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const apply = () => setIsMobile(mediaQuery.matches);

    apply();

    if (mediaQuery.addEventListener) mediaQuery.addEventListener("change", apply);
    else mediaQuery.addListener(apply);

    return () => {
      if (mediaQuery.removeEventListener) mediaQuery.removeEventListener("change", apply);
      else mediaQuery.removeListener(apply);
    };
  }, [breakpoint]);

  return isMobile;
}

const defaultLayout = [
  { i: "user_form", x: 0, y: 0, w: 6, h: 8, minW: 4, minH: 6, maxW: 12 },
  { i: "user_list", x: 6, y: 0, w: 6, h: 5, minW: 6, minH: 4, maxW: 12 },
  { i: "role_stats", x: 6, y: 5, w: 6, h: 3, minW: 6, minH: 3, maxW: 12 },
  { i: "logged_in_users", x: 0, y: 8, w: 2, h: 4, minW: 1, minH: 3, maxW: 6 },
];

const roleProfiles = [
  {
    key: "Super Admin",
    label: "Super Admin",
    description: "Full platform control across administration, audit, system setup, and tenant bootstrap operations.",
  },
  {
    key: "System Admin",
    label: "System Admin",
    description: "Tenant administration role with broad control inside the active tenant boundary.",
  },
  {
    key: "System Manager",
    label: "System Manager",
    description: "Operational manager with oversight of jobs, resources, reports, and customer-facing records.",
  },
  {
    key: "Operator",
    label: "Operator",
    description: "Internal processing user for jobs, ANPR review, uploads, and assigned operational work.",
  },
  {
    key: "User",
    label: "User",
    description: "Standard internal user with limited self-service and assigned-record access.",
  },
  {
    key: "Client",
    label: "Client",
    description: "External portal role limited to their own customer, published jobs, and published documents.",
  },
  {
    key: "Guest",
    label: "Guest",
    description: "Restricted unauthenticated or invitation-stage role. No sensitive data access.",
  },
];

const permissionAreas = [
  {
    key: "identity",
    title: "Identity & Users",
    description: "Users, contacts, portal access, avatars, sessions, and login lifecycle.",
    permissions: [
      { key: "users.view", label: "View users", scope: "Tenant users only" },
      { key: "users.create", label: "Create internal users", scope: "Tenant-derived" },
      { key: "users.update", label: "Edit users", scope: "Tenant users only" },
      { key: "users.delete", label: "Deactivate users", scope: "Tenant users only" },
      { key: "contacts.portal", label: "Provision portal users", scope: "Customer/contact scoped" },
      { key: "sessions.view", label: "View logged-in users", scope: "Tenant sessions only" },
    ],
  },
  {
    key: "customers",
    title: "Customers & Contacts",
    description: "Customer records, contacts, account managers, customer logos, and relationship ownership.",
    permissions: [
      { key: "customers.view", label: "View customers", scope: "Tenant customers only" },
      { key: "customers.create", label: "Create customers", scope: "Tenant-derived" },
      { key: "customers.update", label: "Edit customers", scope: "Tenant customers only" },
      { key: "customers.delete", label: "Archive customers", scope: "Tenant customers only" },
      { key: "contacts.update", label: "Edit contacts", scope: "Tenant/customer scoped" },
      { key: "account_managers.assign", label: "Assign account managers", scope: "Tenant resource scoped" },
    ],
  },
  {
    key: "resources",
    title: "Resources & Roles",
    description: "Tenant-aware resources, user-resource mappings, sales/operator flags, and assignment rules.",
    permissions: [
      { key: "resources.view", label: "View resources", scope: "Tenant resources only" },
      { key: "resources.create", label: "Create resources", scope: "Tenant-derived" },
      { key: "resources.update", label: "Edit resources", scope: "Tenant resources only" },
      { key: "resources.map_users", label: "Map users to resources", scope: "Tenant-safe user/resource FK" },
      { key: "resources.sales", label: "Grant sales capability", scope: "Tenant resources only" },
      { key: "resources.operator", label: "Grant operator capability", scope: "Tenant resources only" },
    ],
  },
  {
    key: "jobs",
    title: "Jobs & Taxonomies",
    description: "Job creation, scheduling, taxonomy hierarchy, workflow binding, and work packages.",
    permissions: [
      { key: "jobs.view", label: "View jobs", scope: "Tenant jobs only" },
      { key: "jobs.create", label: "Create jobs", scope: "Tenant-derived" },
      { key: "jobs.update", label: "Edit jobs", scope: "Tenant jobs only" },
      { key: "jobs.assign", label: "Assign resources", scope: "Tenant resource scoped" },
      { key: "taxonomy.manage", label: "Manage job taxonomy", scope: "Tenant-owned taxonomy" },
      { key: "workflow.bind", label: "Bind workflows/work packages", scope: "Tenant-owned templates" },
    ],
  },
  {
    key: "sales",
    title: "Opportunities & Quotes",
    description: "Sales pipeline, opportunity ownership, quote visibility, approvals, and client-specific access.",
    permissions: [
      { key: "opportunities.view", label: "View opportunities", scope: "DB visibility function" },
      { key: "opportunities.create", label: "Create opportunities", scope: "Requires active resource mapping" },
      { key: "opportunities.update", label: "Edit opportunities", scope: "Owner/access matrix" },
      { key: "quotes.view", label: "View quotes", scope: "DB visibility function" },
      { key: "quotes.create", label: "Create quotes", scope: "Requires sales-capable resource" },
      { key: "quotes.approve", label: "Approve quotes", scope: "Tenant role/resource scoped" },
    ],
  },
  {
    key: "documents",
    title: "Documents & SharePoint",
    description: "Document upload, previews, SharePoint folders, publication, and client portal visibility.",
    permissions: [
      { key: "documents.view", label: "View documents", scope: "Tenant/customer/job scoped" },
      { key: "documents.upload", label: "Upload documents", scope: "Tenant-derived" },
      { key: "documents.preview", label: "Preview documents", scope: "Tenant/customer/job scoped" },
      { key: "documents.publish", label: "Publish to portal", scope: "Customer portal scoped" },
      { key: "sharepoint.manage", label: "Manage SharePoint settings", scope: "Tenant settings only" },
      { key: "documents.delete", label: "Delete documents", scope: "Tenant/customer/job scoped" },
    ],
  },
  {
    key: "reports",
    title: "Reports & Audit",
    description: "Report templates, exports, report logs, audit records, and cross-tenant leakage controls.",
    permissions: [
      { key: "reports.view", label: "View reports", scope: "Tenant reports only" },
      { key: "reports.create", label: "Create report templates", scope: "tenant_id + user_id ownership" },
      { key: "reports.export", label: "Export reports", scope: "Tenant filtered data" },
      { key: "reports.logs", label: "View report logs", scope: "tenant_id + user_id ownership" },
      { key: "audit.view", label: "View audit log", scope: "Tenant audit only" },
      { key: "audit.admin", label: "Run AUD8 validation", scope: "Super/System Admin only" },
    ],
  },
  {
    key: "system",
    title: "System & Bootstrap",
    description: "White-label settings, tenant bootstrap, widget registry, and administration controls.",
    permissions: [
      { key: "theme.manage", label: "Manage theme", scope: "Tenant UI config only" },
      { key: "widgets.manage", label: "Manage widgets", scope: "Tenant-aware registry" },
      { key: "bootstrap.preview", label: "Preview bootstrap batches", scope: "No write until confirmed" },
      { key: "bootstrap.generate", label: "Generate tenant data", scope: "Super Admin only" },
      { key: "bootstrap.cleanup", label: "Safe batch delete", scope: "Tracked generated records only" },
      { key: "system.settings", label: "Edit system settings", scope: "Tenant settings only" },
    ],
  },
];

const defaultRolePermissionMatrix = {
  "Super Admin": "all",
  "System Admin": [
    "users.view", "users.create", "users.update", "contacts.portal", "sessions.view",
    "customers.view", "customers.create", "customers.update", "contacts.update", "account_managers.assign",
    "resources.view", "resources.create", "resources.update", "resources.map_users", "resources.sales", "resources.operator",
    "jobs.view", "jobs.create", "jobs.update", "jobs.assign", "taxonomy.manage", "workflow.bind",
    "opportunities.view", "opportunities.create", "opportunities.update", "quotes.view", "quotes.create", "quotes.approve",
    "documents.view", "documents.upload", "documents.preview", "documents.publish", "sharepoint.manage",
    "reports.view", "reports.create", "reports.export", "reports.logs", "audit.view", "audit.admin",
    "theme.manage", "widgets.manage", "bootstrap.preview", "system.settings",
  ],
  "System Manager": [
    "users.view", "sessions.view", "customers.view", "customers.create", "customers.update", "contacts.update",
    "resources.view", "jobs.view", "jobs.create", "jobs.update", "jobs.assign",
    "opportunities.view", "opportunities.create", "opportunities.update", "quotes.view", "quotes.create",
    "documents.view", "documents.upload", "documents.preview", "documents.publish",
    "reports.view", "reports.export", "reports.logs", "audit.view",
  ],
  Operator: [
    "customers.view", "contacts.update", "resources.view", "jobs.view", "jobs.update",
    "documents.view", "documents.upload", "documents.preview", "reports.view",
  ],
  User: [
    "users.view", "customers.view", "jobs.view", "documents.view", "documents.preview", "reports.view",
  ],
  Client: [
    "customers.view", "jobs.view", "documents.view", "documents.preview", "reports.view",
  ],
  Guest: [],
};

const allPermissionKeys = permissionAreas.flatMap((area) => area.permissions.map((permission) => permission.key));

const expandRolePermissions = (roleKey, matrix = defaultRolePermissionMatrix) => {
  const rolePermissions = matrix[roleKey];
  if (rolePermissions === "all") return [...allPermissionKeys];
  return Array.isArray(rolePermissions) ? [...rolePermissions] : [];
};

const buildEditablePermissionMatrix = (matrix = defaultRolePermissionMatrix) =>
  roleProfiles.reduce((result, role) => {
    result[role.key] = expandRolePermissions(role.key, matrix);
    return result;
  }, {});

const loadPermissionDraft = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY_RBAC_MATRIX));
    if (stored && typeof stored === "object") {
      return roleProfiles.reduce((result, role) => {
        result[role.key] = Array.isArray(stored[role.key])
          ? stored[role.key].filter((permissionKey) => allPermissionKeys.includes(permissionKey))
          : expandRolePermissions(role.key);
        return result;
      }, {});
    }
  } catch {}
  return buildEditablePermissionMatrix();
};

const normaliseLayout = (layout) =>
  (Array.isArray(layout) ? layout : []).map((item) => {
    const currentY = Number.isFinite(item?.y) ? item.y : 0;
    return { ...item, y: Math.max(0, currentY) };
  });

const hasPermission = (roleKey, permissionKey, matrix = defaultRolePermissionMatrix) => {
  const rolePermissions = matrix[roleKey];
  if (rolePermissions === "all") return true;
  return Array.isArray(rolePermissions) && rolePermissions.includes(permissionKey);
};

const UserManagementPage = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const currentUserRole = user?.role || "Guest";
  const tenantId = user?.tenant_id || user?.tenant?.id || null;

  const [widgets, setWidgets] = useState({});
  const [layout, setLayout] = useState([]);
  const [widgetCatalog, setWidgetCatalog] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [gridWidth, setGridWidth] = useState(1200);
  const [activeTab, setActiveTab] = useState(() => localStorage.getItem(STORAGE_KEY_ACTIVE_TAB) || "overview");
  const [selectedRole, setSelectedRole] = useState(() => localStorage.getItem(STORAGE_KEY_RBAC_ROLE) || "System Admin");
  const [selectedAreaKey, setSelectedAreaKey] = useState(() => localStorage.getItem(STORAGE_KEY_RBAC_AREA) || "identity");
  const [rbacView, setRbacView] = useState(() => localStorage.getItem(STORAGE_KEY_RBAC_VIEW) || "policy");
  const [compareRole, setCompareRole] = useState(() => localStorage.getItem(STORAGE_KEY_RBAC_COMPARE_ROLE) || "System Manager");
  const [permissionDraft, setPermissionDraft] = useState(loadPermissionDraft);
  const [permissionSearch, setPermissionSearch] = useState("");
  const [showOnlyDifferences, setShowOnlyDifferences] = useState(false);
  const [rbacNotice, setRbacNotice] = useState(null);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [tenantResources, setTenantResources] = useState([]);
  const [tenantCustomers, setTenantCustomers] = useState([]);
  const [rbacDataStatus, setRbacDataStatus] = useState("idle");
  const gridRef = useRef(null);
  const overviewGridRef = useRef(null);

  const containerPadding = 24;
  const headerHeight = 64;
  const rowHeight = 40;
  const rolesAllowedToAdd = ["Super Admin", "System Admin"];
  const selectedArea = permissionAreas.find((area) => area.key === selectedAreaKey) || permissionAreas[0];
  const selectedRoleProfile = roleProfiles.find((role) => role.key === selectedRole) || roleProfiles[1];
  const compareRoleProfile = roleProfiles.find((role) => role.key === compareRole) || roleProfiles[2];
  const canManageRbac = ["Super Admin", "System Admin"].includes(currentUserRole);
  const selectedRolePermissionCount = permissionDraft[selectedRole]?.length || 0;
  const totalPermissionCount = allPermissionKeys.length;

  const tabButtonStyle = useCallback(
    (tabKey) => {
      const isActive = activeTab === tabKey;

      return {
        padding: "10px 20px",
        cursor: "pointer",
        backgroundColor: isActive ? theme.primary_color : "#f7f7f7",
        color: isActive ? "#fff" : "#333",
        border: "1px solid #ccc",
        borderBottom: isActive ? "1px solid #fff" : "1px solid #ccc",
        borderTopLeftRadius: "8px",
        borderTopRightRadius: "8px",
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        fontWeight: "bold",
        fontFamily: theme.font_body || "var(--font-body)",
        marginBottom: "-1px",
        transition: "background-color 0.2s ease, color 0.2s ease",
      };
    },
    [activeTab, theme]
  );

  const actionButtonStyle = useMemo(
    () => ({
      backgroundColor: theme.button_action?.normal || theme.primary_color || "#000",
      color: "#fff",
      padding: isMobile ? "10px 14px" : "10px 18px",
      minHeight: isMobile ? 44 : undefined,
      borderRadius: "6px",
      fontWeight: "bold",
      fontFamily: "var(--font-body)",
      cursor: "pointer",
      border: "none",
      transition: "background-color 0.2s ease",
    }),
    [isMobile, theme]
  );

  const tenantSummary = useMemo(
    () => [
      { label: "Tenant", value: tenantId || "Current", detail: "All role data must resolve from authenticated current_user context." },
      { label: "Users", value: tenantUsers.length, detail: "Loaded through tenant-scoped user API responses." },
      { label: "Resources", value: tenantResources.length, detail: "Operational role checks depend on active user-resource mappings." },
      { label: "Customers", value: tenantCustomers.length, detail: "Client/portal access must remain customer scoped." },
    ],
    [tenantId, tenantUsers.length, tenantResources.length, tenantCustomers.length]
  );

  useEffect(() => {
    const updateWidth = () => {
      const target = overviewGridRef.current || gridRef.current;
      if (!target) return;
      const nextWidth = Math.floor(target.getBoundingClientRect().width);
      if (nextWidth > 0) setGridWidth(nextWidth);
    };

    updateWidth();

    const observers = [];
    [overviewGridRef.current, gridRef.current].forEach((target) => {
      if (!target || typeof ResizeObserver === "undefined") return;
      const observer = new ResizeObserver(updateWidth);
      observer.observe(target);
      observers.push(observer);
    });

    window.addEventListener("resize", updateWidth);

    return () => {
      observers.forEach((observer) => observer.disconnect());
      window.removeEventListener("resize", updateWidth);
    };
  }, [activeTab]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_ACTIVE_TAB, activeTab);
  }, [activeTab]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_RBAC_ROLE, selectedRole);
  }, [selectedRole]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_RBAC_AREA, selectedAreaKey);
  }, [selectedAreaKey]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_RBAC_VIEW, rbacView);
  }, [rbacView]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_RBAC_COMPARE_ROLE, compareRole);
  }, [compareRole]);

  useEffect(() => {
    try {
      const storedLayout = JSON.parse(localStorage.getItem(STORAGE_KEY_LAYOUT));
      const storedWidgetIds = JSON.parse(localStorage.getItem(STORAGE_KEY_WIDGETS));
      if (Array.isArray(storedLayout) && Array.isArray(storedWidgetIds)) {
        const normalisedLayout = normaliseLayout(storedLayout);
        setLayout(normalisedLayout);
        const newWidgets = {};
        storedWidgetIds.forEach((id) => {
          switch (id) {
            case "user_form":
              newWidgets[id] = <UserFormWidget />;
              break;
            case "user_list":
              newWidgets[id] = <UserListWidget setSelectedUserId={setSelectedUserId} />;
              break;
            case "role_stats":
              newWidgets[id] = <RoleStatsWidget />;
              break;
            case "advanced_user_list":
              newWidgets[id] = <AdvancedUserListWidget setSelectedUserId={setSelectedUserId} />;
              break;
            case "logged_in_users":
              newWidgets[id] = <LoggedInUsersWidget />;
              break;
            default:
              break;
          }
          markWidgetUsedIn(id, "users");
        });
        setWidgets(newWidgets);
        return;
      }
    } catch {}

    setLayout(defaultLayout);
    setWidgets({
      user_form: <UserFormWidget />,
      user_list: <UserListWidget setSelectedUserId={setSelectedUserId} />,
      role_stats: <RoleStatsWidget />,
      logged_in_users: <LoggedInUsersWidget />,
    });
    markWidgetUsedIn("user_form", "users");
    markWidgetUsedIn("user_list", "users");
    markWidgetUsedIn("role_stats", "users");
    markWidgetUsedIn("logged_in_users", "users");
  }, []);

  useEffect(() => {
    axios
      .get("/api/widgets/?category=User Management")
      .then((res) => setWidgetCatalog(res.data))
      .catch(() => {
        setWidgetCatalog([
          {
            id: "user_form",
            name: "user_form",
            friendly_name: "User Form",
            category: "User Management",
            description: "Form to add or edit users",
            is_active: true,
          },
          {
            id: "user_list",
            name: "user_list",
            friendly_name: "User List",
            category: "User Management",
            description: "List of users with roles",
            is_active: true,
          },
          {
            id: "role_stats",
            name: "role_stats",
            friendly_name: "Role Statistics",
            category: "User Management",
            description: "Summary of roles and user counts",
            is_active: true,
          },
          {
            id: "advanced_user_list",
            name: "advanced_user_list",
            friendly_name: "Advanced User List",
            category: "User Management",
            description: "Extended user list with avatar URL display",
            is_active: true,
          },
          {
            id: "logged_in_users",
            name: "logged_in_users",
            friendly_name: "Logged-In Users",
            category: "User Management",
            description: "Expandable widget showing currently logged-in users",
            is_active: true,
          },
        ]);
      });
  }, []);

  useEffect(() => {
    let cancelled = false;
    setRbacDataStatus("loading");

    Promise.allSettled([
      axios.get("/api/users/"),
      axios.get("/api/resources/"),
      axios.get("/api/customers/"),
    ])
      .then(([usersResult, resourcesResult, customersResult]) => {
        if (cancelled) return;

        if (usersResult.status === "fulfilled") {
          const usersData = Array.isArray(usersResult.value.data) ? usersResult.value.data : [];
          setTenantUsers(usersData);
        }

        if (resourcesResult.status === "fulfilled") {
          const resourcesData = Array.isArray(resourcesResult.value.data) ? resourcesResult.value.data : [];
          setTenantResources(resourcesData);
        }

        if (customersResult.status === "fulfilled") {
          const customersData = Array.isArray(customersResult.value.data) ? customersResult.value.data : [];
          setTenantCustomers(customersData);
        }

        setRbacDataStatus("ready");
      })
      .catch(() => {
        if (!cancelled) setRbacDataStatus("error");
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleAddWidget = (widgetId) => {
    if (widgets[widgetId]) {
      alert("Widget already on page");
      return;
    }

    let newWidgetComp;
    switch (widgetId) {
      case "user_form":
        newWidgetComp = <UserFormWidget />;
        break;
      case "user_list":
        newWidgetComp = <UserListWidget setSelectedUserId={setSelectedUserId} />;
        break;
      case "role_stats":
        newWidgetComp = <RoleStatsWidget />;
        break;
      case "advanced_user_list":
        newWidgetComp = <AdvancedUserListWidget setSelectedUserId={setSelectedUserId} />;
        break;
      case "logged_in_users":
        newWidgetComp = <LoggedInUsersWidget />;
        break;
      default:
        alert("Unknown widget");
        return;
    }

    setWidgets((prev) => ({ ...prev, [widgetId]: newWidgetComp }));
    markWidgetUsedIn(widgetId, "users");

    setLayout((prev) => {
      const bottomY = prev.length === 0 ? 0 : Math.max(...prev.map((l) => l.y + l.h));
      const newY = Math.max(0, bottomY);
      const newLayout = [...prev, { i: widgetId, x: 0, y: newY, w: 2, h: 4, minW: 1, minH: 3, maxW: 6 }];
      localStorage.setItem(STORAGE_KEY_LAYOUT, JSON.stringify(newLayout));
      localStorage.setItem(STORAGE_KEY_WIDGETS, JSON.stringify([...Object.keys(widgets), widgetId]));
      return newLayout;
    });
    setShowAddModal(false);
  };

  const handleRemoveWidget = (widgetId) => {
    setWidgets((prev) => {
      const newWidgets = { ...prev };
      delete newWidgets[widgetId];
      return newWidgets;
    });
    setLayout((prev) => {
      const newLayout = prev.filter((item) => item.i !== widgetId);
      localStorage.setItem(STORAGE_KEY_LAYOUT, JSON.stringify(newLayout));
      localStorage.setItem(STORAGE_KEY_WIDGETS, JSON.stringify(Object.keys(widgets).filter((id) => id !== widgetId)));
      return newLayout;
    });
  };

  const onLayoutChange = useCallback(
    (newLayout) => {
      const normalised = normaliseLayout(newLayout);
      setLayout(normalised);
      try {
        localStorage.setItem(STORAGE_KEY_LAYOUT, JSON.stringify(normalised));
        localStorage.setItem(STORAGE_KEY_WIDGETS, JSON.stringify(Object.keys(widgets)));
      } catch {}
    },
    [widgets]
  );

  const mobileOverviewItems = useMemo(() => {
    const widgetIds = new Set(Object.keys(widgets));

    const positioned = layout
      .filter((item) => widgetIds.has(item.i))
      .slice()
      .sort((a, b) => {
        const ay = Number.isFinite(Number(a?.y)) ? Number(a.y) : Number.MAX_SAFE_INTEGER;
        const by = Number.isFinite(Number(b?.y)) ? Number(b.y) : Number.MAX_SAFE_INTEGER;
        if (ay !== by) return ay - by;

        const ax = Number.isFinite(Number(a?.x)) ? Number(a.x) : Number.MAX_SAFE_INTEGER;
        const bx = Number.isFinite(Number(b?.x)) ? Number(b.x) : Number.MAX_SAFE_INTEGER;
        if (ax !== bx) return ax - bx;

        return String(a?.i || "").localeCompare(String(b?.i || ""));
      });

    const positionedIds = new Set(positioned.map((item) => item.i));
    const missing = Object.keys(widgets)
      .filter((id) => !positionedIds.has(id))
      .map((id) => ({ i: id, x: 0, y: Number.MAX_SAFE_INTEGER, w: 6, h: 4 }));

    return [...positioned, ...missing];
  }, [layout, widgets]);

  const getMobileWidgetHeight = useCallback(
    (item) => {
      const h = Number.isFinite(Number(item?.h)) ? Math.max(1, Number(item.h)) : 4;
      return Math.max(280, h * rowHeight + Math.max(0, h - 1) * 24);
    },
    [rowHeight]
  );

  const renderTabBar = () =>
    isMobile ? (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 8,
          marginBottom: 10,
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 10,
          padding: 6,
        }}
      >
        {[
          { key: "overview", label: "Overview" },
          { key: "rbac", label: "RBAC Console" },
        ].map((tab) => {
          const active = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              style={{
                minHeight: 44,
                borderRadius: 8,
                border: active ? `1px solid ${theme.primary_color || "#7c3aed"}` : "1px solid transparent",
                background: active ? (theme.primary_color || "#7c3aed") : "#f8fafc",
                color: active ? "#fff" : "#374151",
                fontWeight: 800,
                cursor: "pointer",
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
    ) : (
      <div style={{ display: "flex", gap: "10px", marginBottom: 0 }}>
        <button type="button" onClick={() => setActiveTab("overview")} style={tabButtonStyle("overview")}>
          Overview
        </button>
        <button type="button" onClick={() => setActiveTab("rbac")} style={tabButtonStyle("rbac")}>
          RBAC Console
        </button>
      </div>
    );

  const renderOverviewGrid = () => (
    <div
      style={{
        backgroundColor: "#ffffff",
        border: "1px solid #e5e7eb",
        borderRadius: isMobile ? 12 : "0 0 14px 14px",
        minHeight: isMobile ? 0 : "calc(100vh - 190px)",
        overflow: "hidden",
        boxShadow: "0 8px 20px rgba(15,23,42,0.05)",
      }}
    >
      <section
        style={{
          minHeight: isMobile ? 0 : "calc(100vh - 190px)",
          padding: isMobile ? 10 : 0,
          minWidth: 0,
          backgroundColor: "#f9fafb",
        }}
      >
        <div ref={overviewGridRef} style={{ width: "100%", minWidth: 0 }}>
          {isMobile ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 12, width: "100%", minWidth: 0 }}>
              {mobileOverviewItems.map((item) => (
                <div
                  key={item.i}
                  style={{
                    width: "100%",
                    minWidth: 0,
                    height: `${getMobileWidgetHeight(item)}px`,
                    overflow: "hidden",
                  }}
                >
                  <WidgetWrapper
                    title={widgetCatalog.find((w) => w.name === item.i)?.friendly_name || item.i}
                    onRemove={() => handleRemoveWidget(item.i)}
                  >
                    {widgets[item.i]}
                  </WidgetWrapper>
                </div>
              ))}
            </div>
          ) : (
            <GridLayout
              className="layout"
              layout={layout}
              cols={12}
              rowHeight={rowHeight}
              width={gridWidth}
              draggableHandle=".widget-drag-handle"
              resizeHandles={["se"]}
              isResizable
              isDraggable
              onLayoutChange={onLayoutChange}
              compactType={null}
              preventCollision={false}
              margin={[16, 24]}
            >
              {Object.entries(widgets).map(([key, WidgetComp]) => (
                <div key={key}>
                  <WidgetWrapper
                    title={widgetCatalog.find((w) => w.name === key)?.friendly_name || key}
                    onRemove={() => handleRemoveWidget(key)}
                  >
                    {WidgetComp}
                  </WidgetWrapper>
                </div>
              ))}
            </GridLayout>
          )}
        </div>
      </section>

      {showAddModal && (
        <AddWidgetModal
          widgets={widgetCatalog}
          onClose={() => setShowAddModal(false)}
          onSelect={handleAddWidget}
          categoryFilter="User Management"
        />
      )}
    </div>
  );

  const isDraftPermissionAllowed = useCallback(
    (roleKey, permissionKey) => hasPermission(roleKey, permissionKey, permissionDraft),
    [permissionDraft]
  );

  const toggleDraftPermission = useCallback(
    (roleKey, permissionKey) => {
      if (!canManageRbac) return;
      setPermissionDraft((current) => {
        const rolePermissions = new Set(current[roleKey] || []);
        if (rolePermissions.has(permissionKey)) rolePermissions.delete(permissionKey);
        else rolePermissions.add(permissionKey);
        return { ...current, [roleKey]: Array.from(rolePermissions) };
      });
      setRbacNotice({ type: "info", message: "Draft changed locally. Use Save Draft to retain it in this browser." });
    },
    [canManageRbac]
  );

  const setAreaDraftPermissions = useCallback(
    (roleKey, enabled) => {
      if (!canManageRbac) return;
      const areaKeys = selectedArea.permissions.map((permission) => permission.key);
      setPermissionDraft((current) => {
        const next = new Set(current[roleKey] || []);
        areaKeys.forEach((permissionKey) => {
          if (enabled) next.add(permissionKey);
          else next.delete(permissionKey);
        });
        return { ...current, [roleKey]: Array.from(next) };
      });
      setRbacNotice({ type: "info", message: `${enabled ? "Enabled" : "Disabled"} all permissions in ${selectedArea.title} for the draft.` });
    },
    [canManageRbac, selectedArea]
  );

  const savePermissionDraft = useCallback(() => {
    if (!canManageRbac) return;
    localStorage.setItem(STORAGE_KEY_RBAC_MATRIX, JSON.stringify(permissionDraft));
    setRbacNotice({ type: "success", message: "RBAC draft saved locally. Backend persistence is the next implementation stage." });
  }, [canManageRbac, permissionDraft]);

  const resetPermissionDraft = useCallback(() => {
    if (!canManageRbac) return;
    if (!window.confirm("Reset the RBAC draft to the baseline role matrix?")) return;
    const baseline = buildEditablePermissionMatrix();
    setPermissionDraft(baseline);
    localStorage.removeItem(STORAGE_KEY_RBAC_MATRIX);
    setRbacNotice({ type: "success", message: "RBAC draft reset to the baseline role matrix." });
  }, [canManageRbac]);

  const filteredAreaPermissions = useMemo(() => {
    const search = permissionSearch.trim().toLowerCase();
    return selectedArea.permissions.filter((permission) => {
      const selectedAllowed = isDraftPermissionAllowed(selectedRole, permission.key);
      const compareAllowed = isDraftPermissionAllowed(compareRole, permission.key);
      if (showOnlyDifferences && selectedAllowed === compareAllowed) return false;
      if (!search) return true;
      return [permission.key, permission.label, permission.scope]
        .join(" ")
        .toLowerCase()
        .includes(search);
    });
  }, [compareRole, isDraftPermissionAllowed, permissionSearch, selectedArea.permissions, selectedRole, showOnlyDifferences]);

  const renderPermissionPill = (allowed) => (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 78,
        padding: "6px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        color: allowed ? "#065f46" : "#6b7280",
        backgroundColor: allowed ? "#d1fae5" : "#f3f4f6",
        border: allowed ? "1px solid #a7f3d0" : "1px solid #e5e7eb",
      }}
    >
      {allowed ? "Allowed" : "Blocked"}
    </span>
  );

  const renderRbacConsole = () => {
    const viewButtons = [
      { key: "policy", label: "Policy Editor" },
      { key: "roles", label: "Roles" },
      { key: "directory", label: "Directory" },
      { key: "effective", label: "Effective Access" },
      { key: "audit", label: "Audit & Controls" },
    ];

    const panelStyle = {
      border: "1px solid #e5e7eb",
      borderRadius: 12,
      backgroundColor: "#fff",
      overflow: "hidden",
    };

    const renderPolicyEditor = () => (
      <>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "center", flexWrap: "wrap", marginBottom: 14 }}>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <input
              value={permissionSearch}
              onChange={(event) => setPermissionSearch(event.target.value)}
              placeholder="Filter permissions, actions, or scope"
              style={{ height: isMobile ? 44 : 40, width: isMobile ? "100%" : 330, maxWidth: "100%", border: "1px solid #d1d5db", borderRadius: 8, padding: "0 12px", fontSize: isMobile ? 16 : undefined }}
            />
            <label style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 13, fontWeight: 700, color: "#4b5563" }}>
              <input type="checkbox" checked={showOnlyDifferences} onChange={(event) => setShowOnlyDifferences(event.target.checked)} />
              Differences only
            </label>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button type="button" disabled={!canManageRbac} onClick={() => setAreaDraftPermissions(selectedRole, true)} style={{ ...actionButtonStyle, padding: "8px 12px", opacity: canManageRbac ? 1 : 0.45 }}>Allow Area</button>
            <button type="button" disabled={!canManageRbac} onClick={() => setAreaDraftPermissions(selectedRole, false)} style={{ padding: "8px 12px", borderRadius: 7, border: "1px solid #d1d5db", background: "#fff", fontWeight: 800, cursor: canManageRbac ? "pointer" : "not-allowed", opacity: canManageRbac ? 1 : 0.45 }}>Block Area</button>
          </div>
        </div>

        <div style={panelStyle}>
          {isMobile ? (
            <div style={{ display: "flex", flexDirection: "column" }}>
              {filteredAreaPermissions.length === 0 ? (
                <div style={{ padding: 24, textAlign: "center", color: "#6b7280" }}>
                  No permissions match the current filter.
                </div>
              ) : (
                filteredAreaPermissions.map((permission) => {
                  const allowed = isDraftPermissionAllowed(selectedRole, permission.key);
                  const compareAllowed = isDraftPermissionAllowed(compareRole, permission.key);
                  return (
                    <div key={permission.key} style={{ padding: 14, borderTop: "1px solid #e5e7eb" }}>
                      <div style={{ fontWeight: 850, color: "#111827" }}>{permission.label}</div>
                      <div style={{ fontFamily: "monospace", fontSize: 11, color: "#64748b", marginTop: 3, wordBreak: "break-word" }}>
                        {permission.key}
                      </div>
                      <div style={{ color: "#4b5563", fontSize: 13, marginTop: 8 }}>{permission.scope}</div>

                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 8,
                          marginTop: 12,
                        }}
                      >
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 8,
                            minHeight: 44,
                            padding: "8px 10px",
                            borderRadius: 8,
                            border: "1px solid #e5e7eb",
                            background: "#f8fafc",
                            fontSize: 12,
                            fontWeight: 800,
                          }}
                        >
                          <span>{selectedRole}</span>
                          <input
                            type="checkbox"
                            checked={allowed}
                            disabled={!canManageRbac}
                            onChange={() => toggleDraftPermission(selectedRole, permission.key)}
                            style={{ width: 22, height: 22, cursor: canManageRbac ? "pointer" : "not-allowed" }}
                            aria-label={`${selectedRole}: ${permission.label}`}
                          />
                        </label>

                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 8,
                            minHeight: 44,
                            padding: "8px 10px",
                            borderRadius: 8,
                            border: "1px solid #e5e7eb",
                            background: "#f8fafc",
                            fontSize: 12,
                            fontWeight: 800,
                          }}
                        >
                          <span>{compareRole}</span>
                          {renderPermissionPill(compareAllowed)}
                        </div>
                      </div>

                      <div style={{ color: "#6b7280", fontSize: 12, marginTop: 10, lineHeight: 1.45 }}>
                        {allowed ? "Role allows the action; backend scope must still pass." : "Role blocks the action; API denial remains mandatory."}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ backgroundColor: "#f8fafc", borderBottom: "1px solid #e5e7eb" }}>
                  <th style={{ textAlign: "left", padding: "12px 14px", fontSize: 12, color: "#64748b", textTransform: "uppercase" }}>Permission</th>
                  <th style={{ textAlign: "left", padding: "12px 14px", fontSize: 12, color: "#64748b", textTransform: "uppercase" }}>Scope / condition</th>
                  <th style={{ textAlign: "center", padding: "12px 14px", fontSize: 12, color: "#64748b", textTransform: "uppercase" }}>{selectedRole}</th>
                  <th style={{ textAlign: "center", padding: "12px 14px", fontSize: 12, color: "#64748b", textTransform: "uppercase" }}>{compareRole}</th>
                  <th style={{ textAlign: "left", padding: "12px 14px", fontSize: 12, color: "#64748b", textTransform: "uppercase" }}>Enforcement note</th>
                </tr>
              </thead>
              <tbody>
                {filteredAreaPermissions.length === 0 ? (
                  <tr><td colSpan={5} style={{ padding: 24, textAlign: "center", color: "#6b7280" }}>No permissions match the current filter.</td></tr>
                ) : filteredAreaPermissions.map((permission) => {
                  const allowed = isDraftPermissionAllowed(selectedRole, permission.key);
                  const compareAllowed = isDraftPermissionAllowed(compareRole, permission.key);
                  return (
                    <tr key={permission.key} style={{ borderTop: "1px solid #e5e7eb" }}>
                      <td style={{ padding: "12px 14px" }}>
                        <div style={{ fontWeight: 850, color: "#111827" }}>{permission.label}</div>
                        <div style={{ fontFamily: "monospace", fontSize: 11, color: "#64748b", marginTop: 3 }}>{permission.key}</div>
                      </td>
                      <td style={{ padding: "12px 14px", color: "#4b5563", fontSize: 13 }}>{permission.scope}</td>
                      <td style={{ padding: "12px 14px", textAlign: "center" }}>
                        <input
                          type="checkbox"
                          checked={allowed}
                          disabled={!canManageRbac}
                          onChange={() => toggleDraftPermission(selectedRole, permission.key)}
                          style={{ width: 18, height: 18, cursor: canManageRbac ? "pointer" : "not-allowed" }}
                          aria-label={`${selectedRole}: ${permission.label}`}
                        />
                      </td>
                      <td style={{ padding: "12px 14px", textAlign: "center" }}>{renderPermissionPill(compareAllowed)}</td>
                      <td style={{ padding: "12px 14px", color: "#6b7280", fontSize: 12 }}>
                        {allowed ? "Role allows the action; backend scope must still pass." : "Role blocks the action; API denial remains mandatory."}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </>
    );

    const renderRoles = () => (
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: 12 }}>
        {roleProfiles.map((role) => {
          const count = permissionDraft[role.key]?.length || 0;
          const active = role.key === selectedRole;
          return (
            <button
              type="button"
              key={role.key}
              onClick={() => { setSelectedRole(role.key); setRbacView("policy"); }}
              style={{ ...panelStyle, textAlign: "left", padding: 16, cursor: "pointer", borderColor: active ? (theme.accent_color || theme.primary_color || "#7c3aed") : "#e5e7eb", boxShadow: active ? "0 0 0 2px rgba(124,58,237,0.10)" : "none" }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
                <div style={{ fontSize: 17, fontWeight: 900, color: "#111827" }}>{role.label}</div>
                <span style={{ padding: "5px 9px", borderRadius: 999, background: "#f3f4f6", color: "#374151", fontSize: 12, fontWeight: 800 }}>{count}/{totalPermissionCount}</span>
              </div>
              <div style={{ marginTop: 8, color: "#4b5563", fontSize: 13, lineHeight: 1.5 }}>{role.description}</div>
              <div style={{ marginTop: 12, height: 6, background: "#e5e7eb", borderRadius: 999, overflow: "hidden" }}>
                <div style={{ width: `${totalPermissionCount ? (count / totalPermissionCount) * 100 : 0}%`, height: "100%", background: theme.primary_color || "#7c3aed" }} />
              </div>
            </button>
          );
        })}
      </div>
    );

    const renderDirectory = () => (
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, minmax(0, 1fr))", gap: 14 }}>
        <div style={panelStyle}>
          <div style={{ padding: 14, borderBottom: "1px solid #e5e7eb", fontWeight: 900 }}>Tenant users ({tenantUsers.length})</div>
          <div style={{ maxHeight: 480, overflow: "auto" }}>
            {tenantUsers.length === 0 ? <div style={{ padding: 18, color: "#6b7280" }}>No tenant users returned.</div> : tenantUsers.slice(0, 100).map((item) => (
              <div key={item.id || item.email} style={{ padding: "11px 14px", borderTop: "1px solid #f1f5f9" }}>
                <div style={{ fontWeight: 800 }}>{item.full_name || item.name || item.username || item.email || `User ${item.id}`}</div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 3 }}>{item.role || "Role not returned"}{item.email ? ` • ${item.email}` : ""}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={panelStyle}>
          <div style={{ padding: 14, borderBottom: "1px solid #e5e7eb", fontWeight: 900 }}>Resources ({tenantResources.length})</div>
          <div style={{ maxHeight: 480, overflow: "auto" }}>
            {tenantResources.length === 0 ? <div style={{ padding: 18, color: "#6b7280" }}>No tenant resources returned.</div> : tenantResources.slice(0, 100).map((item) => (
              <div key={item.id || item.name} style={{ padding: "11px 14px", borderTop: "1px solid #f1f5f9" }}>
                <div style={{ fontWeight: 800 }}>{item.name || item.full_name || `Resource ${item.id}`}</div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 3 }}>User mapping: {item.user_id || "none"} • Sales: {item.is_sales ? "yes" : "no"} • Operator: {item.is_operator ? "yes" : "no"}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={panelStyle}>
          <div style={{ padding: 14, borderBottom: "1px solid #e5e7eb", fontWeight: 900 }}>Customers ({tenantCustomers.length})</div>
          <div style={{ maxHeight: 480, overflow: "auto" }}>
            {tenantCustomers.length === 0 ? <div style={{ padding: 18, color: "#6b7280" }}>No tenant customers returned.</div> : tenantCustomers.slice(0, 100).map((item) => (
              <div key={item.id || item.name} style={{ padding: "11px 14px", borderTop: "1px solid #f1f5f9" }}>
                <div style={{ fontWeight: 800 }}>{item.name || item.company_name || `Customer ${item.id}`}</div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 3 }}>Customer ID: {item.id || "—"}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );

    const renderEffectiveAccess = () => (
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "320px 1fr", gap: 14 }}>
        <div style={{ ...panelStyle, padding: 16 }}>
          <div style={{ fontSize: 12, textTransform: "uppercase", color: "#64748b", fontWeight: 900 }}>Inspection subject</div>
          <div style={{ fontSize: 20, fontWeight: 900, marginTop: 6 }}>{selectedRoleProfile.label}</div>
          <div style={{ color: "#4b5563", fontSize: 13, marginTop: 8, lineHeight: 1.5 }}>{selectedRoleProfile.description}</div>
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid #e5e7eb", fontSize: 13, color: "#4b5563" }}>
            <strong>{selectedRolePermissionCount}</strong> of <strong>{totalPermissionCount}</strong> draft permissions are allowed.
          </div>
        </div>
        <div style={panelStyle}>
          {permissionAreas.map((area) => {
            const allowed = area.permissions.filter((permission) => isDraftPermissionAllowed(selectedRole, permission.key));
            return (
              <div key={area.key} style={{ padding: 14, borderTop: "1px solid #e5e7eb" }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                  <div>
                    <div style={{ fontWeight: 900 }}>{area.title}</div>
                    <div style={{ color: "#64748b", fontSize: 12, marginTop: 3 }}>{allowed.map((permission) => permission.label).join(" • ") || "No access"}</div>
                  </div>
                  <span style={{ fontWeight: 900, color: allowed.length ? "#065f46" : "#6b7280" }}>{allowed.length}/{area.permissions.length}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );

    const renderAudit = () => (
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: 14 }}>
        {[
          ["Tenant boundary", "Tenant identity must come from the authenticated current_user context. Frontend tenant_id values are never authoritative."],
          ["Role enforcement", "Navigation hiding and disabled controls improve UX but do not constitute security. Every API operation must deny unauthorised roles."],
          ["Resource conditions", "Operational permissions may additionally require an active user-to-resource mapping, sales capability, ownership, or explicit access rows."],
          ["Customer and portal scope", "Portal identities must remain limited to their customer, published jobs, approved documents, and explicit portal capabilities."],
          ["Audit attribution", "Every RBAC mutation must write actor user_id, tenant_id, old values, new values, target role/group, and correlation data to audit_log."],
          ["Current implementation state", "This console now provides a functional local policy draft and inspection experience. Server-side role/group persistence remains to be wired."],
        ].map(([title, body]) => (
          <div key={title} style={{ ...panelStyle, padding: 16 }}>
            <div style={{ fontWeight: 900, color: "#111827" }}>{title}</div>
            <div style={{ marginTop: 7, color: "#4b5563", fontSize: 13, lineHeight: 1.55 }}>{body}</div>
          </div>
        ))}
      </div>
    );

    return (
      <div style={{ backgroundColor: "#ffffff", border: "1px solid #e5e7eb", borderRadius: isMobile ? 12 : "0 0 14px 14px", minHeight: isMobile ? 0 : "calc(100vh - 190px)", overflow: "hidden", boxShadow: "0 8px 20px rgba(15,23,42,0.05)" }}>
        <div style={{ padding: isMobile ? 10 : "14px 18px 0", borderBottom: "1px solid #e5e7eb", background: "#fff" }}>
          {isMobile ? (
            <select
              value={rbacView}
              onChange={(event) => setRbacView(event.target.value)}
              style={{
                width: "100%",
                height: 46,
                border: "1px solid #d1d5db",
                borderRadius: 8,
                padding: "0 10px",
                fontSize: 16,
                fontWeight: 800,
                background: "#fff",
              }}
            >
              {viewButtons.map((view) => (
                <option key={view.key} value={view.key}>{view.label}</option>
              ))}
            </select>
          ) : (
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
              {viewButtons.map((view) => {
                const active = rbacView === view.key;
                return (
                  <button key={view.key} type="button" onClick={() => setRbacView(view.key)} style={{ padding: "10px 2px 12px", border: "none", borderBottom: active ? `3px solid ${theme.primary_color || "#7c3aed"}` : "3px solid transparent", background: "transparent", color: active ? (theme.primary_color || "#7c3aed") : "#4b5563", fontWeight: active ? 900 : 700, cursor: "pointer" }}>
                    {view.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "260px minmax(0, 1fr)",
            minHeight: isMobile ? 0 : "calc(100vh - 245px)",
          }}
        >
          <aside
            style={{
              borderRight: isMobile ? "none" : "1px solid #e5e7eb",
              borderBottom: isMobile ? "1px solid #e5e7eb" : "none",
              background: "#f8fafc",
              padding: isMobile ? 10 : 14,
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 900, color: "#64748b", marginBottom: 9, textTransform: "uppercase", letterSpacing: ".05em" }}>Roles</div>
            <select value={selectedRole} onChange={(event) => setSelectedRole(event.target.value)} style={{ width: "100%", height: 40, border: "1px solid #d1d5db", borderRadius: 8, padding: "0 9px", fontWeight: 800, background: "#fff" }}>
              {roleProfiles.map((role) => <option key={role.key} value={role.key}>{role.label}</option>)}
            </select>

            <div style={{ fontSize: 11, fontWeight: 900, color: "#64748b", margin: "18px 0 9px", textTransform: "uppercase", letterSpacing: ".05em" }}>Compare with</div>
            <select value={compareRole} onChange={(event) => setCompareRole(event.target.value)} style={{ width: "100%", height: 40, border: "1px solid #d1d5db", borderRadius: 8, padding: "0 9px", fontWeight: 800, background: "#fff" }}>
              {roleProfiles.filter((role) => role.key !== selectedRole).map((role) => <option key={role.key} value={role.key}>{role.label}</option>)}
            </select>

            <div style={{ fontSize: 11, fontWeight: 900, color: "#64748b", margin: "18px 0 9px", textTransform: "uppercase", letterSpacing: ".05em" }}>Policy areas</div>
            {isMobile ? (
              <select
                value={selectedAreaKey}
                onChange={(event) => {
                  setSelectedAreaKey(event.target.value);
                  setRbacView("policy");
                }}
                style={{
                  width: "100%",
                  height: 44,
                  border: "1px solid #d1d5db",
                  borderRadius: 8,
                  padding: "0 9px",
                  fontWeight: 800,
                  background: "#fff",
                }}
              >
                {permissionAreas.map((area) => {
                  const allowedCount = area.permissions.filter((permission) => isDraftPermissionAllowed(selectedRole, permission.key)).length;
                  return (
                    <option key={area.key} value={area.key}>
                      {area.title} ({allowedCount}/{area.permissions.length})
                    </option>
                  );
                })}
              </select>
            ) : (
              <nav style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                {permissionAreas.map((area) => {
                  const active = area.key === selectedAreaKey;
                  const allowedCount = area.permissions.filter((permission) => isDraftPermissionAllowed(selectedRole, permission.key)).length;
                  return (
                    <button key={area.key} type="button" onClick={() => { setSelectedAreaKey(area.key); setRbacView("policy"); }} style={{ display: "flex", justifyContent: "space-between", gap: 8, textAlign: "left", padding: "10px 10px", borderRadius: 8, border: active ? `1px solid ${theme.accent_color || theme.primary_color || "#7c3aed"}` : "1px solid transparent", background: active ? "#fff" : "transparent", color: active ? (theme.primary_color || "#111827") : "#374151", fontWeight: active ? 900 : 650, cursor: "pointer" }}>
                      <span>{area.title}</span><span style={{ fontSize: 11, color: "#64748b" }}>{allowedCount}/{area.permissions.length}</span>
                    </button>
                  );
                })}
              </nav>
            )}
          </aside>

          <section style={{ padding: isMobile ? 12 : 18, minWidth: 0, background: "#fff" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: "#111827" }}>{rbacView === "policy" ? selectedArea.title : viewButtons.find((view) => view.key === rbacView)?.label}</h3>
                <p style={{ margin: "6px 0 0", color: "#64748b", maxWidth: 820, lineHeight: 1.5 }}>
                  {rbacView === "policy" ? selectedArea.description : "Tenant-aware role, directory, and access inspection for Vision Systems."}
                </p>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <button type="button" onClick={resetPermissionDraft} disabled={!canManageRbac} style={{ padding: "9px 12px", border: "1px solid #d1d5db", borderRadius: 7, background: "#fff", fontWeight: 800, opacity: canManageRbac ? 1 : 0.45, cursor: canManageRbac ? "pointer" : "not-allowed" }}>Reset Draft</button>
                <button type="button" onClick={savePermissionDraft} disabled={!canManageRbac} style={{ ...actionButtonStyle, opacity: canManageRbac ? 1 : 0.45, cursor: canManageRbac ? "pointer" : "not-allowed" }}>Save Draft</button>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, minmax(0, 1fr))" : "repeat(4, minmax(0, 1fr))", gap: 10, marginBottom: 16 }}>
              {[
                ["Tenant", tenantId || "Current", "Authenticated tenant context"],
                ["Users", tenantUsers.length, rbacDataStatus === "loading" ? "Loading directory" : "Tenant-scoped API"],
                ["Role permissions", `${selectedRolePermissionCount}/${totalPermissionCount}`, selectedRoleProfile.label],
                ["Comparison", compareRoleProfile.label, "Read-only comparison"],
              ].map(([label, value, detail]) => (
                <div key={label} style={{ ...panelStyle, padding: 12 }}>
                  <div style={{ color: "#64748b", fontSize: 11, fontWeight: 900, textTransform: "uppercase" }}>{label}</div>
                  <div style={{ fontSize: 19, fontWeight: 900, marginTop: 4, color: theme.primary_color || "#111827" }}>{value}</div>
                  <div style={{ color: "#64748b", fontSize: 11, marginTop: 3 }}>{detail}</div>
                </div>
              ))}
            </div>

            {rbacNotice && (
              <div style={{ marginBottom: 14, padding: "10px 12px", borderRadius: 8, border: `1px solid ${rbacNotice.type === "success" ? "#86efac" : "#bfdbfe"}`, background: rbacNotice.type === "success" ? "#f0fdf4" : "#eff6ff", color: rbacNotice.type === "success" ? "#166534" : "#1e40af", fontSize: 13, fontWeight: 650 }}>
                {rbacNotice.message}
              </div>
            )}

            {!canManageRbac && (
              <div style={{ marginBottom: 14, padding: "10px 12px", borderRadius: 8, border: "1px solid #fcd34d", background: "#fffbeb", color: "#92400e", fontSize: 13 }}>
                Read-only mode. Only Super Admin and System Admin roles may alter the local RBAC draft.
              </div>
            )}

            {rbacView === "policy" && renderPolicyEditor()}
            {rbacView === "roles" && renderRoles()}
            {rbacView === "directory" && renderDirectory()}
            {rbacView === "effective" && renderEffectiveAccess()}
            {rbacView === "audit" && renderAudit()}
          </section>
        </div>
      </div>
    );
  };

  return (
    <div
      className={isMobile ? undefined : "p-6"}
      style={{
        backgroundColor: "#f0f0f0",
        minHeight: isMobile ? "100%" : "100vh",
        overflowX: "hidden",
        paddingLeft: isMobile ? 12 : containerPadding,
        paddingRight: isMobile ? 12 : containerPadding,
        paddingTop: isMobile ? 6 : undefined,
        paddingBottom: isMobile ? 16 : undefined,
        boxSizing: "border-box",
      }}
    >
      <div ref={gridRef} className="w-full" style={{ minWidth: 0 }}>
        <div
          className={isMobile ? undefined : "flex justify-between items-center mb-1"}
          style={{
            height: isMobile ? "auto" : headerHeight,
            minHeight: isMobile ? 58 : undefined,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 10,
          }}
        >
          <h2
            className={isMobile ? undefined : "text-2xl font-semibold"}
            style={{ margin: 0, fontSize: isMobile ? "1.35rem" : undefined, fontWeight: 600 }}
          >
            User Management
          </h2>
          {activeTab === "overview" && rolesAllowedToAdd.includes(currentUserRole) && (
            <button
              type="button"
              onClick={() => setShowAddModal(true)}
              style={actionButtonStyle}
              onMouseEnter={(e) => (e.target.style.backgroundColor = theme.button_action?.hover || "#333")}
              onMouseLeave={(e) => (e.target.style.backgroundColor = theme.button_action?.normal || theme.primary_color || "#000")}
              onMouseDown={(e) => (e.target.style.backgroundColor = theme.button_action?.click || "#111")}
              onMouseUp={(e) => (e.target.style.backgroundColor = theme.button_action?.hover || "#333")}
              data-testid="add-widget-button"
            >
              Add Widget
            </button>
          )}
        </div>

        {renderTabBar()}
        {activeTab === "overview" ? renderOverviewGrid() : renderRbacConsole()}
      </div>

      {selectedUserId && <ManageUserModal userId={selectedUserId} onClose={() => setSelectedUserId(null)} />}
    </div>
  );
};

export default UserManagementPage;
